import type { StorageManager } from '@/storage/save-data';
import type { LevelData } from '@/game/level';
import type { LevelSyncStatus, SyncState, ConflictResolution } from './sync-types';
import * as api from './api-client';
import { DownloadedRepo } from './downloaded-repo';

export class SyncManager {
  private repo: DownloadedRepo;
  constructor(private sm: StorageManager) {
    this.repo = new DownloadedRepo(sm);
  }

  async checkSyncStatuses(levels: LevelData[]): Promise<Map<string, LevelSyncStatus>> {
    const map = new Map<string, LevelSyncStatus>();

    // Seed local-only entries
    for (const l of levels) {
      if (!l.meta.remoteId) {
        map.set(l.meta.id, {
          levelId: l.meta.id, remoteId: null, state: 'local_only', isPublic: false,
        });
      }
    }

    // Query server for the ones with remoteIds
    const remote = levels.filter((l) => !!l.meta.remoteId);
    if (remote.length === 0) return map;

    const sess = this.requireSession();
    const payload = remote.map((l) => ({
      remoteId: l.meta.remoteId!,
      remoteVersion: l.meta.remoteVersion ?? 0,
    }));
    const response = await api.syncStatuses(sess.token, { levels: payload });

    for (const l of remote) {
      const rid = l.meta.remoteId!;
      const srv = response.statuses[rid];
      const dirty = !!l.meta.remoteDirty;
      const localVer = l.meta.remoteVersion ?? 0;

      let state: SyncState;
      if (!srv || srv.deleted) {
        state = 'removed_from_server';
      } else if (srv.serverVersion === localVer && !dirty) {
        state = 'synced';
      } else if (srv.serverVersion > localVer && !dirty) {
        state = 'update_available';
      } else if (srv.serverVersion === localVer && dirty) {
        state = 'edited';
      } else if (srv.serverVersion > localVer && dirty) {
        state = 'conflict';
      } else {
        // localVer > serverVersion (shouldn't normally happen). Treat as edited.
        state = 'edited';
      }

      map.set(l.meta.id, {
        levelId: l.meta.id,
        remoteId: rid,
        state,
        isPublic: srv?.isPublic ?? false,
        serverVersion: srv?.serverVersion,
        verifier: srv?.verifier ?? null,
        currentVersionCompletionCount: srv?.currentVersionCompletionCount ?? 0,
      });

      // Refresh cached verifier snapshot for downloaded levels.
      if (l.meta.source?.kind !== 'builtin' && srv && !srv.deleted) {
        this.repo.updateCache(rid, {
          verifier: srv.verifier ?? null,
          currentVersionCompletionCount: srv.currentVersionCompletionCount ?? 0,
          cachedAt: Date.now(),
        });
      }
    }

    return map;
  }

  async pushLevel(level: LevelData): Promise<LevelData> {
    const sess = this.requireSession();
    const expectedRemoteVersion = level.meta.remoteId ? (level.meta.remoteVersion ?? 0) : null;
    // Strip server-derived fields from outgoing payload — they're populated by API responses
    // and should never round-trip through push.
    const { verifier: _v, currentVersionCompletionCount: _c, ...outgoingMeta } = level.meta;
    const outgoing: LevelData = { ...level, meta: outgoingMeta };
    const r = await api.pushLevel(sess.token, {
      level: outgoing,
      expectedRemoteVersion,
    });
    return {
      ...level,
      meta: {
        ...level.meta,
        remoteId: r.remoteId,
        remoteVersion: r.version,
        remoteDirty: false,
      },
    };
  }

  async pullLevel(localLevel: LevelData): Promise<LevelData> {
    const sess = this.requireSession();
    if (!localLevel.meta.remoteId) throw new Error('Cannot pull a level without remoteId');
    return this.pullByRemoteId(localLevel.meta.remoteId);
  }

  /**
   * Pull a level by remoteId without an existing local copy — used when Level Select
   * discovers a server-only level via `listMineLevels()` and the user taps PULL.
   */
  async pullByRemoteId(remoteId: string): Promise<LevelData> {
    const sess = this.requireSession();
    const r = await api.pullLevel(sess.token, remoteId);
    return {
      ...r.level,
      meta: {
        ...r.level.meta,
        remoteId,
        remoteVersion: r.version,
        remoteDirty: false,
      },
    };
  }

  /** List the owner's levels on the server (for two-device discovery). */
  async listMineLevels(): Promise<Array<{
    remoteId: string;
    slug: string;
    name: string;
    difficulty: string;
    modeFlags: number;
    version: number;
    sizeBytes: number;
    isPublic: boolean;
    updatedAt: number;
  }>> {
    const sess = this.requireSession();
    const r = await api.minelevels(sess.token);
    return r.items.map((i) => ({
      remoteId: i.remoteId,
      slug: i.slug,
      name: i.name,
      difficulty: i.difficulty,
      modeFlags: i.modeFlags,
      version: i.version,
      sizeBytes: i.sizeBytes,
      isPublic: i.isPublic,
      updatedAt: i.updatedAt,
    }));
  }

  async browseLevels(filters: {
    sort?: 'newest' | 'popular' | 'updated';
    difficulties?: string[];
    mode?: 'real' | 'complex' | 'mixed';
    tags?: string[];
    author?: string;
    cursor?: string;
    limit?: number;
    includeUnverified?: boolean;
  } = {}): Promise<{ items: unknown[]; nextCursor: string | null }> {
    const sess = this.requireSession();
    const params: Record<string, string | string[]> = {};
    if (filters.sort) params.sort = filters.sort;
    if (filters.difficulties && filters.difficulties.length) params.difficulty = filters.difficulties;
    if (filters.mode) params.mode = filters.mode;
    if (filters.tags && filters.tags.length) params.tag = filters.tags;
    if (filters.author) params.author = filters.author;
    if (filters.cursor) params.cursor = filters.cursor;
    if (filters.limit != null) params.limit = String(filters.limit);
    if (filters.includeUnverified) params.includeUnverified = '1';
    return api.browse(sess.token, params);
  }

  async browseByAuthor(userId: string, filters: {
    sort?: 'newest' | 'popular' | 'updated';
    cursor?: string;
    limit?: number;
  } = {}): Promise<{ items: unknown[]; nextCursor: string | null }> {
    return this.browseLevels({ ...filters, author: userId });
  }

  async listCompletions(remoteId: string, opts: { cursor?: string; limit?: number } = {}) {
    const sess = this.requireSession();
    return api.listCompletions(sess.token, remoteId, opts);
  }

  async remixLevel(sourceRemoteId: string): Promise<LevelData> {
    const sess = this.requireSession();
    const r = await api.remixLevel(sess.token, sourceRemoteId);
    // Server response shape: { level, version, remoteId, ownerDisplayName, isPublic }
    const lvl = (r as unknown as { level: LevelData; version: number; remoteId: string }).level;
    const rid = (r as unknown as { remoteId: string }).remoteId ?? lvl.meta.remoteId;
    const ver = (r as unknown as { version: number }).version ?? 1;
    return {
      ...lvl,
      meta: {
        ...lvl.meta,
        remoteId: rid,
        remoteVersion: ver,
        remoteDirty: false,
      },
    };
  }

  async downloadLevel(remoteId: string): Promise<LevelData> {
    const sess = this.requireSession();
    const r = await api.pullLevel(sess.token, remoteId);
    return {
      ...r.level,
      meta: {
        ...r.level.meta,
        remoteId,
        remoteVersion: r.version,
        remoteDirty: false,
      },
    };
  }

  async publishLevel(level: LevelData, isPublic: boolean): Promise<void> {
    const sess = this.requireSession();
    if (!level.meta.remoteId) throw new Error('Cannot publish a level without remoteId');
    await api.patchVisibility(sess.token, level.meta.remoteId, { isPublic });
  }

  async deleteRemoteLevel(level: LevelData): Promise<void> {
    const sess = this.requireSession();
    if (!level.meta.remoteId) throw new Error('Cannot delete remote for a level without remoteId');
    await api.deleteLevel(sess.token, level.meta.remoteId);
  }

  async resolveConflict(
    local: LevelData,
    resolution: 'keep_both' | 'keep_mine' | 'keep_server',
  ): Promise<ConflictResolution> {
    const sess = this.requireSession();
    const rid = local.meta.remoteId;
    if (!rid) throw new Error('No remoteId — nothing to resolve');

    // Always fetch the latest server version first.
    const srv = await api.pullLevel(sess.token, rid);

    if (resolution === 'keep_server') {
      return {
        kept: {
          ...srv.level,
          meta: { ...srv.level.meta, remoteId: rid, remoteVersion: srv.version, remoteDirty: false },
        },
      };
    }

    if (resolution === 'keep_mine') {
      // Force-push local with the current server version as expected.
      const pushed = await api.pushLevel(sess.token, {
        level: local,
        expectedRemoteVersion: srv.version,
      });
      return {
        kept: {
          ...local,
          meta: { ...local.meta, remoteId: pushed.remoteId, remoteVersion: pushed.version, remoteDirty: false },
        },
      };
    }

    // keep_both: duplicate local as a new level (strip remote fields, rename), then pull server into original slot.
    const duplicate: LevelData = {
      ...local,
      meta: {
        ...local.meta,
        id: `${local.meta.id}-copy-${Date.now().toString(36)}`,
        name: `${local.meta.name} (copy)`,
        remoteId: undefined,
        remoteVersion: undefined,
        remoteDirty: undefined,
      },
    };
    const kept: LevelData = {
      ...srv.level,
      meta: { ...srv.level.meta, remoteId: rid, remoteVersion: srv.version, remoteDirty: false },
    };
    return { kept, duplicate };
  }

  getSession() {
    return this.sm.getSession();
  }

  private requireSession() {
    const s = this.sm.getSession();
    if (!s) throw new Error('Not signed in');
    return s;
  }
}
